/** Ler nuvens de pontos XYZ e PTS que não cabem na memória.
 *
 *  O caso que obrigou a isto: um PTS de 16,4 GB com 230 milhões de
 *  pontos. O leitor antigo fazia `file.text()`, que pede ao browser o
 *  ficheiro inteiro como UMA cadeia de texto. O JavaScript não tem
 *  cadeias desse tamanho (o limite anda por meio gigabyte a um), e o
 *  Chrome recusa com um `NotReadableError`, que depois nós traduzíamos
 *  como "o ficheiro está numa pasta sincronizada". Não estava: estava
 *  nas Transferências, e era simplesmente grande de mais.
 *
 *  Mesmo que se conseguisse ler, 230 milhões de pontos não se desenham
 *  num browser: só as posições dão 2,7 GB, a cor outro tanto. Nenhum
 *  visualizador web mostra isso em bruto.
 *
 *  Por isso isto faz duas coisas ao mesmo tempo:
 *
 *  1. **Lê em fluxo**, um pedaço de cada vez, sem nunca ter o ficheiro
 *     inteiro em memória.
 *  2. **Guarda um em cada N pontos**, com N escolhido para o resultado
 *     caber num alvo que o browser desenha bem. E diz que o fez: mostrar
 *     5 milhões de 230 e deixar a pessoa pensar que vê tudo seria
 *     mentir sobre o levantamento.
 *
 *  Este ficheiro tem as partes puras: dividir em linhas através das
 *  fronteiras dos pedaços, ler um ponto de uma linha, decidir o N. O
 *  cano está em `lerNuvemEmFluxo`, mais abaixo.
 */

/** As densidades que se oferecem, em pontos guardados.
 *
 *  Começou por ser um número fixo, cinco milhões, escolhido para caber
 *  em qualquer computador. Com um levantamento de 230 milhões isso é um
 *  ponto em cada 46, e o Rui achou-o grosseiro, com razão: quem tem uma
 *  máquina capaz não tinha como pedir mais. Agora escolhe-se.
 *
 *  Cada ponto ocupa 15 bytes depois de lido (12 da posição, 3 da cor):
 *  5 milhões são 75 MB, 40 milhões são 600 MB. O que pesa mais do que a
 *  memória é desenhá-los: a partir dos 20 milhões convém uma placa
 *  gráfica dedicada. */
export const DENSIDADES = [5_000_000, 10_000_000, 20_000_000, 40_000_000] as const

/** Dez milhões: o dobro do que havia, e ainda confortável numa máquina
 *  de escritório. */
export const ALVO_POR_OMISSAO = 10_000_000

/** Bytes por ponto já lido: posição em 3 floats, cor em 3 bytes. */
export const BYTES_POR_PONTO = 15

/** A partir de que distância à origem (em unidades do ficheiro) as
 *  posições passam a ser guardadas relativas a uma origem local.
 *
 *  As posições vão para a GPU em float32, que tem 24 bits de mantissa.
 *  Em coordenadas de projecto isso arredonda: em UTM 29N (Norte perto de
 *  4 300 000) os pontos caem em degraus de 0,5 m; em PT-TM06 (±300 km)
 *  em degraus de 1,6 a 3 cm. Abaixo de 10 km o degrau é de 1 mm ou
 *  menos, e aí não se mexe em nada: os números ficam exactamente como
 *  eram. Acima, subtrai-se a origem em float64 antes de passar a float32
 *  e devolve-se a origem, que o objecto 3D leva na sua matriz. */
export const ORIGEM_LOCAL_A_PARTIR_DE = 10_000

/** A origem local para uma nuvem cujo primeiro ponto é `p`: zero quando
 *  as coordenadas são pequenas, o ponto arredondado ao metro quando são
 *  grandes. */
export function origemPara(p: { x: number; y: number; z: number }): [number, number, number] {
  const longe = Math.max(Math.abs(p.x), Math.abs(p.y), Math.abs(p.z)) >= ORIGEM_LOCAL_A_PARTIR_DE
  return longe ? [Math.round(p.x), Math.round(p.y), Math.round(p.z)] : [0, 0, 0]
}

/**
 * Parte texto em linhas, mesmo quando um pedaço acaba a meio de uma.
 *
 * Um ficheiro lido aos pedaços parte-se onde calha: a meio de um
 * número, entre o `\r` e o `\n`. A linha partida fica guardada e junta-
 * -se ao princípio do pedaço seguinte. Sem isto, cada fronteira
 * produzia um ponto errado, e num ficheiro de 16 GB lido em pedaços de
 * 64 kB isso são 250 mil pontos com coordenadas inventadas.
 */
export class LeitorDeLinhas {
  private resto = ''

  /** Devolve as linhas completas que este pedaço fecha. */
  empurrar(pedaco: string): string[] {
    const texto = this.resto + pedaco
    const linhas = texto.split('\n')
    // O último bocado pode estar a meio: fica para a próxima.
    this.resto = linhas.pop() ?? ''
    return linhas.map((l) => (l.endsWith('\r') ? l.slice(0, -1) : l))
  }

  /** O que sobrou no fim do ficheiro, se o ficheiro não acabar em `\n`. */
  fechar(): string[] {
    const r = this.resto
    this.resto = ''
    if (r.length === 0) return []
    return [r.endsWith('\r') ? r.slice(0, -1) : r]
  }
}

/**
 * A linha de contagem do PTS: um número inteiro sozinho na primeira
 * linha, às vezes com espaços à frente ou atrás.
 */
export function contagemDeclarada(linha: string): number | null {
  const t = linha.trim()
  if (!/^\d+$/.test(t)) return null
  const n = Number(t)
  return Number.isSafeInteger(n) && n > 0 ? n : null
}

/**
 * Um em cada quantos pontos se guarda, para caber no alvo.
 *
 * Nunca menos de um: um ficheiro mais pequeno do que o alvo guarda-se
 * inteiro, e não se inventam pontos.
 */
export function passoPara(total: number, alvo: number): number {
  if (!Number.isFinite(total) || total <= 0) return 1
  if (!Number.isFinite(alvo) || alvo <= 0) return 1
  return Math.max(1, Math.ceil(total / alvo))
}

/**
 * Estima quantos pontos tem um ficheiro sem cabeçalho.
 *
 * Mede-se o comprimento médio das primeiras linhas e divide-se o
 * tamanho do ficheiro por ele. Não é exacto, e não precisa de ser: só
 * serve para escolher o passo, e um passo um pouco ao lado dá um
 * resultado um pouco acima ou abaixo do alvo, que é inofensivo.
 */
export function estimarPontos(tamanhoBytes: number, bytesPorLinha: number): number {
  if (!(bytesPorLinha > 0)) return 0
  return Math.round(tamanhoBytes / bytesPorLinha)
}

/** Em que coluna começa a cor, conforme o número de colunas.
 *
 *  - 3: só x y z, sem cor.
 *  - 6: x y z r g b.
 *  - 7 ou mais: x y z intensidade r g b, e às vezes as normais a seguir
 *    (o PTS do Revit traz 10 colunas: x y z i r g b nx ny nz).
 *
 *  Ler a cor da coluna 3 num PTS dava a intensidade como vermelho, e
 *  foi o primeiro defeito que este leitor teve. */
export function inicioDaCor(colunas: number): number | null {
  if (colunas >= 7) return 4
  if (colunas === 6) return 3
  return null
}

export interface Ponto {
  x: number
  y: number
  z: number
  /** A cor em bruto, na escala em que o ficheiro a escreve. */
  cor: [number, number, number] | null
}

/**
 * Lê um ponto de uma linha, ou null se a linha não for um ponto.
 *
 * Linhas vazias, comentários e a linha de contagem do PTS não são
 * pontos. Uma linha com lixo também não, e ignora-se em vez de parar a
 * leitura: num ficheiro de 230 milhões de linhas, uma linha estragada
 * não é razão para perder as outras.
 */
export function lerPonto(linha: string): Ponto | null {
  const t = linha.trim()
  if (t.length === 0 || t.startsWith('#')) return null
  const partes = t.split(/[\s,;]+/)
  if (partes.length < 3) return null
  const x = Number(partes[0])
  const y = Number(partes[1])
  const z = Number(partes[2])
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null
  }
  const i = inicioDaCor(partes.length)
  let cor: [number, number, number] | null = null
  if (i !== null) {
    const r = Number(partes[i])
    const g = Number(partes[i + 1])
    const b = Number(partes[i + 2])
    if ([r, g, b].every(Number.isFinite)) cor = [r, g, b]
  }
  return { x, y, z, cor }
}

export interface ResultadoDaLeitura {
  /** Posições relativas a `origem` (ver ORIGEM_LOCAL_A_PARTIR_DE). */
  posicoes: Float32Array
  /** A origem local, nas coordenadas do ficheiro. [0, 0, 0] quando as
   *  coordenadas são pequenas e as posições vêm tal e qual. */
  origem: [number, number, number]
  /** 0..255 em três bytes por ponto, ou null se o ficheiro não trouxer
   *  cor. A GPU lê-os normalizados para 0..1. Eram floats de 32 bits,
   *  quatro vezes mais, e era isso que não deixava caber mais pontos. */
  cores: Uint8Array | null
  guardados: number
  /** Quantos pontos o ficheiro tinha, contados ao passar. */
  lidos: number
  /** Um em cada quantos se guardou. 1 é tudo. */
  passo: number
  /** Quantos o cabeçalho dizia que havia, se dizia. */
  declarados: number | null
}

export interface Progresso {
  bytesLidos: number
  bytesTotal: number
  guardados: number
}

export interface OpcoesDaLeitura {
  alvo?: number
  aoProgredir?: (p: Progresso) => void
  sinal?: AbortSignal
}

/**
 * Lê um XYZ ou PTS de qualquer tamanho, guardando um em cada N pontos.
 *
 * Nunca pede o ficheiro inteiro. Lê pelo `stream()`, decodifica aos
 * pedaços e guarda só os pontos que o passo manda guardar. A memória
 * que usa é a do resultado, não a do ficheiro.
 */
export async function lerNuvemEmFluxo(
  ficheiro: Blob,
  opcoes: OpcoesDaLeitura = {},
): Promise<ResultadoDaLeitura> {
  const alvo = opcoes.alvo ?? ALVO_POR_OMISSAO
  const total = ficheiro.size

  // Reserva-se logo o que vai ser preciso, em vez de começar em um
  // milhão e duplicar. Cada duplicação copiava tudo o que já estava lido,
  // e a 40 milhões isso eram duas cópias de centenas de megabytes a meio
  // da leitura. O tamanho do ficheiro dá um tecto: nenhuma linha de
  // ponto tem menos de uns 30 bytes, portanto um ficheiro pequeno não
  // reserva 40 milhões de lugares vazios.
  let capacidade = Math.max(1024, Math.min(alvo + 1024, Math.ceil(total / 30)))
  let posicoes = new Float32Array(capacidade * 3)
  let cores = new Float32Array(capacidade * 3)
  let temCor = false
  let guardados = 0
  let lidos = 0
  let passo = 0 // 0 = ainda por decidir
  let declarados: number | null = null
  let primeiraLinha = true
  let bytesLidos = 0
  // Para estimar o passo quando o ficheiro não traz cabeçalho.
  let bytesDasAmostras = 0
  let linhasDasAmostras = 0
  const pendentes: Ponto[] = []

  const crescer = () => {
    capacidade *= 2
    const p = new Float32Array(capacidade * 3)
    p.set(posicoes)
    posicoes = p
    const c = new Float32Array(capacidade * 3)
    c.set(cores)
    cores = c
  }

  let origem: [number, number, number] | null = null
  const guardar = (pt: Ponto) => {
    if (guardados >= capacidade) crescer()
    // A subtracção faz-se em float64, antes de o valor passar a float32.
    origem ??= origemPara(pt)
    const k = guardados * 3
    posicoes[k] = pt.x - origem[0]
    posicoes[k + 1] = pt.y - origem[1]
    posicoes[k + 2] = pt.z - origem[2]
    if (pt.cor) {
      temCor = true
      cores[k] = pt.cor[0]
      cores[k + 1] = pt.cor[1]
      cores[k + 2] = pt.cor[2]
    } else {
      cores[k] = -1
    }
    guardados += 1
  }

  const decidirPasso = () => {
    if (passo > 0) return
    const peloTamanho = estimarPontos(
      total,
      bytesDasAmostras / Math.max(1, linhasDasAmostras),
    )
    // Com cabeçalho, acredita-se no MENOR dos dois. Um cabeçalho que
    // diz mais pontos do que o ficheiro consegue ter está errado, e
    // acontece: um ficheiro cortado ou uma transferência interrompida
    // continuam a dizer "230 milhões" na primeira linha. Acreditar nele
    // deitava fora 46 de cada 47 pontos de um ficheiro que cabia
    // inteiro, e foi o nosso próprio teste que o apanhou.
    const estimado =
      declarados !== null && peloTamanho > 0
        ? Math.min(declarados, peloTamanho)
        : (declarados ?? peloTamanho)
    passo = passoPara(estimado, alvo)
    // Os pontos lidos antes de se saber o passo aplicam-no agora.
    for (let i = 0; i < pendentes.length; i += 1) {
      if (i % passo === 0) guardar(pendentes[i])
    }
    pendentes.length = 0
  }

  const tratar = (linha: string) => {
    if (primeiraLinha) {
      primeiraLinha = false
      const n = contagemDeclarada(linha)
      if (n !== null) {
        declarados = n
        return
      }
    }
    const pt = lerPonto(linha)
    if (!pt) return
    if (passo === 0) {
      bytesDasAmostras += linha.length + 1
      linhasDasAmostras += 1
      pendentes.push(pt)
      // Espera-se sempre por umas mil linhas, mesmo com cabeçalho: é o
      // que dá o comprimento médio para confirmar o que o cabeçalho diz.
      if (linhasDasAmostras >= 1000) decidirPasso()
      lidos += 1
      return
    }
    if (lidos % passo === 0) guardar(pt)
    lidos += 1
  }

  const leitor = new LeitorDeLinhas()
  const descodificador = new TextDecoder()
  const fluxo = ficheiro.stream().getReader()
  try {
    for (;;) {
      if (opcoes.sinal?.aborted) {
        throw new DOMException('Leitura cancelada.', 'AbortError')
      }
      const { value, done } = await fluxo.read()
      if (done) break
      bytesLidos += value.byteLength
      const texto = descodificador.decode(value, { stream: true })
      for (const l of leitor.empurrar(texto)) tratar(l)
      opcoes.aoProgredir?.({ bytesLidos, bytesTotal: total, guardados })
    }
    for (const l of leitor.empurrar(descodificador.decode())) tratar(l)
    for (const l of leitor.fechar()) tratar(l)
  } finally {
    fluxo.releaseLock()
  }

  // Ficheiro com menos de mil linhas: nunca se chegou a decidir o passo
  // pelo caminho. Decide-se agora, com o que se viu, e aplica-se aos
  // pontos que ficaram à espera. Guardar tudo sem perguntar ignorava o
  // alvo sempre que o ficheiro fosse curto.
  if (passo === 0) decidirPasso()

  if (guardados === 0) {
    throw new Error('O ficheiro não contém pontos XYZ válidos.')
  }

  // A escala da cor decide-se sobre o que se guardou: 0..255 ou 0..1.
  // Decidir linha a linha fazia um ponto escuro (0 0 1 em 0..255) passar
  // por 0..1 e sair azul vivo.
  let coresFinais: Uint8Array | null = null
  if (temCor) {
    let maximo = 0
    for (let i = 0; i < guardados * 3; i += 1) {
      if (cores[i] > maximo) maximo = cores[i]
    }
    // Para 0..255, que é o que um byte guarda. Um ficheiro em 0..1 sobe;
    // um em 0..255 fica como está.
    const factor = maximo > 1 ? 1 : 255
    coresFinais = new Uint8Array(guardados * 3)
    const neutro = [184, 199, 219]
    const byte = (v: number) => Math.max(0, Math.min(255, Math.round(v * factor)))
    for (let i = 0; i < guardados; i += 1) {
      const k = i * 3
      if (cores[k] < 0) {
        coresFinais[k] = neutro[0]
        coresFinais[k + 1] = neutro[1]
        coresFinais[k + 2] = neutro[2]
      } else {
        coresFinais[k] = byte(cores[k])
        coresFinais[k + 1] = byte(cores[k + 1])
        coresFinais[k + 2] = byte(cores[k + 2])
      }
    }
  }

  return {
    posicoes: posicoes.slice(0, guardados * 3),
    origem: origem ?? [0, 0, 0],
    cores: coresFinais,
    guardados,
    lidos,
    passo,
    declarados,
  }
}

/** O que se diz depois de ler, sem esconder que se reduziu. */
export function resumoDaLeitura(r: ResultadoDaLeitura): string {
  const milhoes = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1).replace('.', ',')} milhões`
      : n >= 1000
        ? `${Math.round(n / 1000)} mil`
        : String(n)
  if (r.passo <= 1) return `${milhoes(r.guardados)} pontos.`
  return (
    `${milhoes(r.guardados)} pontos mostrados de ${milhoes(r.lidos)}: ` +
    `um em cada ${r.passo}. O browser não desenha o levantamento inteiro, ` +
    'mas a forma do edifício está toda lá.'
  )
}
